import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

// With refetchOnMount/refetchOnWindowFocus off globally, a query that ends in
// error never retries by itself — one transient blip (API deploy restart,
// brief connection drop) leaves the screen dead until a full page reload. So
// this view quietly retries the failed queries a few times with backoff, and
// offers a reload button for the impatient/persistent cases.
const RETRY_DELAYS_MS = [3_000, 10_000, 30_000];

export function ErrorView(): React.ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const attemptRef = useRef(0);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let unmounted = false;

    function scheduleNext() {
      if (unmounted) return;
      const delay = RETRY_DELAYS_MS[attemptRef.current];
      if (delay === undefined) return;
      timer = setTimeout(() => {
        attemptRef.current += 1;
        void queryClient
          .refetchQueries({
            type: "active",
            predicate: (query) => query.state.status === "error",
          })
          .finally(scheduleNext);
      }, delay);
    }

    scheduleNext();
    return () => {
      unmounted = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [queryClient]);

  // Render
  return (
    <div className="w-full h-full flex flex-col gap-4 justify-center items-center text-center">
      <div>
        {t("error")}.<br />
        {t("compMisc.tryRefreshing")}
      </div>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 rounded-md border border-gray-400 bg-white font-semibold hover:bg-gray-50"
      >
        {t("compMisc.refreshPage")}
      </button>
    </div>
  );
}
